import {Component, computed, effect, inject, input, output} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {distinctUntilChanged} from 'rxjs';
import {Card} from 'primeng/card';
import {TranslocoDirective} from '@jsverse/transloco';
import {TooltipModule} from 'primeng/tooltip';
import {FormFieldComponent} from '../../../../../../../shared/components/inputs/form-field/form-field.component';
import {DirectusTooltipComponent} from '../../../../../../../shared/components/directus-tooltip/directus-tooltip.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../shared/project-step-save-button/project-step-save-button.component';

const DEFAULT_MAX_PRICE_TO_GET_POINTS = 4;

interface NormalizationValues {
  minPriceOffer?: number;
  maxPriceToGetPoints?: number;
}

@Component({
  selector: 'app-normalization-range-tab',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Card,
    TranslocoDirective,
    TooltipModule,
    FormFieldComponent,
    DirectusTooltipComponent,
    ProjectStepSaveButtonComponent,
  ],
  templateUrl: './step-1-3-normalization-range-tab.component.html',
})
export class Step13NormalizationRangeTabComponent {
  private readonly fb = inject(FormBuilder);

  readonly originalValues = input<NormalizationValues | null>(null);
  readonly isSaving = input(false);
  readonly disabled = input<boolean>(false);

  readonly submitOutput = output<NormalizationValues>();

  readonly normalizationForm: FormGroup = this.fb.group({
    minPriceOffer: [
      {value: 1, disabled: true},
      [Validators.required, Validators.min(1.0), Validators.max(1.0)],
    ],
    maxPriceToGetPoints: [
      DEFAULT_MAX_PRICE_TO_GET_POINTS,
      [Validators.required, Validators.min(1.1), Validators.max(5)],
    ],
  });

  private readonly formValueSignal = toSignal(
    this.normalizationForm.valueChanges.pipe(
      distinctUntilChanged((prev, curr) => {
        return (
          prev?.minPriceOffer === curr?.minPriceOffer &&
          prev?.maxPriceToGetPoints === curr?.maxPriceToGetPoints
        );
      }),
    ),
    {
      initialValue: this.normalizationForm.value,
    },
  );

  readonly hasChanges = computed(() => {
    const original = this.originalValues();
    const current = this.formValueSignal();

    if (!original) {
      return true;
    }

    const originalMax = parseFloat((original.maxPriceToGetPoints ?? 0).toFixed(1));
    const currentMax = parseFloat((current?.maxPriceToGetPoints ?? 0).toFixed(1));

    return originalMax !== currentMax;
  });

  constructor() {
    this.watchOriginalValuesAndPatchForm();
  }

  private watchOriginalValuesAndPatchForm() {
    effect(() => {
      const original = this.originalValues();
      if (original) {
        this.normalizationForm.patchValue({
          minPriceOffer: original.minPriceOffer ?? 1,
          maxPriceToGetPoints: original.maxPriceToGetPoints ?? DEFAULT_MAX_PRICE_TO_GET_POINTS,
        });
      }
    });
  }

  onSubmit(): void {
    if (this.normalizationForm.invalid) return;
    const {minPriceOffer, maxPriceToGetPoints} = this.normalizationForm.value;
    this.submitOutput.emit({minPriceOffer, maxPriceToGetPoints});
  }
}
